import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { change } from "redux-form";

import ImageInput from "./ImageInput";
import ImageThumbnail from "./ImageThumbnail";

export default function ImageFieldArray(props){
	const dispatch = useDispatch();
	let {
		fields,
		libraryId,
		form,
		inputPrompt,
		maxAssets,
		modalTitle,
		accept,
	} = props;

	let existingSelection = [...(fields.getAll() || [])]; /*
		`fields.getAll()` returns an object that will update what's displayed in the form, but not
		update the state if you call array actions like `push` on it, like it would if you had called
		`fields.push()`.

		It's not just a copy of the values, so we have to copy it ourselves for safety.
	*/
	let existingSelectionIds = existingSelection.map(value => value.id);

	function onSelect(selectedIds, images){
		/*
			We need to store names in order to be able to display the thumbnails, because the URL
			depends on the libraryId and the asset name, which complicates merging new values with
			existing values, and will require us to do some cleanup before submitting, because
			the generate versions API only wants the image ids.
		*/

		// Just set up a hash for constant time lookup, rather than searching `images` repeatedly
		let imagesById = images.reduce((prev, image) => {
			prev[image.id] = image;
			return prev;
		}, {});

		let selectedImages = selectedIds.map(id => imagesById[id]);

		let selectedSet = new Set(existingSelectionIds);

		let newValue = selectedImages.reduce((prev, image) => {
			/*
				We use a reducer just to do one form value change at the end, instead of pushing a
				bunch of intermediate changes with `fields.push`, which would retrigger selectors, etc.
			*/
			if(!selectedSet.has(image.id)){
				prev.push(image);
			}
			return prev;
		}, existingSelection);

		dispatch(change(form, fields.name, newValue));
	}

	const shouldDisplayInput = useMemo(() => {
		if(!maxAssets) { return true; }
		return existingSelection.length < maxAssets;
	}, [existingSelection, maxAssets]);


	let $thumbnails = fields.map((name, index, fields) => {
		let image = fields.get(index);
		let onClear = () => { fields.remove(index); };
		return (
			<div
				key = {image.name}
				className = "image-field-array-item"
			>
				<ImageThumbnail
					name = {image.name}
					libraryId = {libraryId}
					onClear = {onClear}
				/>
			</div>
		);
	});

	return (
		<div className="image-field-array">
			{$thumbnails}
			{shouldDisplayInput && (
				<ImageInput
					onSelectAssets = {onSelect}
					selectedIds = {existingSelectionIds}
					inputPrompt = {inputPrompt}
					modalTitle = {modalTitle}
					accept = {accept}
				/>
			)}
		</div>
	);
}

ImageFieldArray.displayName = "ImageFieldArray";

ImageFieldArray.propTypes = {
	fields: PropTypes.object, // This is array-like, but is an object from redux-form
	libraryId: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number,
	]),
	form: PropTypes.string.isRequired,
	inputPrompt: PropTypes.node,
	modalTitle: PropTypes.node,
	accept: PropTypes.string,
};
